import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Droppable,
  Draggable,
  DropResult,
  DragDropContext,
} from "react-beautiful-dnd";

import { RiskTemplate } from "types";
import TextInput from "components/inputs/TextInput";
import TextArea from "components/inputs/TextAreaInput";
import Collapsible from "components/Collapsible/Collapsible";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { useConfirmation } from "context/Confirmation";
import { ControlContext } from "context/Controls";
import { GroupingTemplateOptions } from "./GroupingTemplateOptions";
import { RiskGroupingTemplateMemberRow } from "./RiskGroupingTemplateMemberRow";
import { AddRiskTemplateToGroupModal } from "./AddRiskTemplateToGroupModal";
import { useUpdateRiskGroupingTemplate } from "components/Risk/hooks/useUpdateRiskGroupingTemplate";
import { useDeleteRiskGroupingTemplate } from "components/Risk/hooks/useDeleteRiskGroupingTemplate";
import { useCreateRiskTemplateForGroup } from "components/Risk/hooks/useCreateRiskTemplateForGroup";

export const UNGROUPED_TEMPLATE_GROUP = "UngroupedTemplates";

interface RiskGroupingTemplate {
  crn?: string;
  id: string;
  label: string;
  description: string;
  members: string[];
}

interface RiskGroupingTemplatesProps {
  groupings: RiskGroupingTemplate[];
  riskTemplates: RiskTemplate[];
}

export const RiskGroupingTemplates: React.FC<RiskGroupingTemplatesProps> = ({
  groupings,
  riskTemplates,
}) => {
  const { t } = useTranslation();
  const { openConfirmation } = useConfirmation();
  const { displayNotification } = useContext(ControlContext);
  const updateGroupingTemplate = useUpdateRiskGroupingTemplate();
  const deleteGroupingTemplate = useDeleteRiskGroupingTemplate();
  const createRiskTemplateForGroup = useCreateRiskTemplateForGroup();

  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [groups, setGroups] = useState<RiskGroupingTemplate[]>([]);
  const [showOptionsForGroup, setShowOptionsForGroup] = useState<
    string | null
  >(null);
  const [addingMemberToGroup, setAddingMemberToGroup] =
    useState<RiskGroupingTemplate | null>(null);

  const optionsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const buildGroups = useCallback(
    (gs: RiskGroupingTemplate[], rts: RiskTemplate[]) => {
      const normalized = gs.map((g) => ({ ...g, members: g.members || [] }));
      const alreadyMapped = normalized.flatMap((g) => g.members);
      const ungrouped: RiskGroupingTemplate = {
        id: UNGROUPED_TEMPLATE_GROUP,
        label: t("riskTemplate.groupings.ungrouped_label"),
        description: t("riskTemplate.groupings.ungrouped_description"),
        members: rts
          .filter((rt) => !alreadyMapped.includes(rt.crn))
          .map((rt) => rt.crn),
      };
      return [ungrouped, ...normalized];
    },
    [t]
  );

  useEffect(() => {
    setGroups(buildGroups(groupings, riskTemplates));
  }, [groupings, riskTemplates, buildGroups]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showOptionsForGroup &&
        optionsRef.current &&
        buttonRef.current[showOptionsForGroup] &&
        !optionsRef.current.contains(event.target as Node) &&
        !buttonRef.current[showOptionsForGroup]?.contains(event.target as Node)
      ) {
        setShowOptionsForGroup(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOptionsForGroup]);

  const onDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;
    if (!destination) return;

    if (type === "GROUP") {
      // Local-only reorder (no order field in backend)
      const newGroups = Array.from(groups);
      const [reorderedGroup] = newGroups.splice(source.index, 1);
      newGroups.splice(destination.index, 0, reorderedGroup);
      setGroups(newGroups);
    } else if (type === "RISK") {
      const sourceGroup = groups.find((g) => g.id === source.droppableId);
      const destGroup = groups.find((g) => g.id === destination.droppableId);
      if (!sourceGroup || !destGroup) return;

      if (sourceGroup.id === destGroup.id) {
        // Reorder within same group
        const newMembers = Array.from(sourceGroup.members);
        const [moved] = newMembers.splice(source.index, 1);
        newMembers.splice(destination.index, 0, moved);
        const updatedGroup = { ...sourceGroup, crn: sourceGroup.crn!, members: newMembers };
        const newGroups = groups.map((g) =>
          g.id === sourceGroup.id ? updatedGroup : g
        );
        setGroups(newGroups);
        if (sourceGroup.id !== UNGROUPED_TEMPLATE_GROUP) {
          updateGroupingTemplate.mutate(updatedGroup);
        }
      } else {
        // Move across groups
        const newSourceMembers = Array.from(sourceGroup.members);
        const newDestMembers = Array.from(destGroup.members);
        const [moved] = newSourceMembers.splice(source.index, 1);
        newDestMembers.splice(destination.index, 0, moved);

        const updatedSource = { ...sourceGroup, crn: sourceGroup.crn!, members: newSourceMembers };
        const updatedDest = { ...destGroup, crn: destGroup.crn!, members: newDestMembers };

        const newGroups = groups.map((g) => {
          if (g.id === sourceGroup.id) return updatedSource;
          if (g.id === destGroup.id) return updatedDest;
          return g;
        });
        setGroups(newGroups);

        const promises: Promise<any>[] = [];
        if (sourceGroup.id !== UNGROUPED_TEMPLATE_GROUP) {
          promises.push(
            new Promise((resolve, reject) =>
              updateGroupingTemplate.mutate(updatedSource, {
                onSuccess: resolve,
                onError: reject,
              })
            )
          );
        }
        if (destGroup.id !== UNGROUPED_TEMPLATE_GROUP) {
          promises.push(
            new Promise((resolve, reject) =>
              updateGroupingTemplate.mutate(updatedDest, {
                onSuccess: resolve,
                onError: reject,
              })
            )
          );
        }
        Promise.all(promises).catch(() => {
          // On failure, rebuild from props (refetch will reconcile)
          setGroups(buildGroups(groupings, riskTemplates));
        });
      }
    }
  };

  const handleEdit = useCallback((group: RiskGroupingTemplate) => {
    setEditingGroup(group.id);
    setEditedName(group.label);
    setEditedDescription(group.description || "");
  }, []);

  const handleSave = useCallback(
    (group: RiskGroupingTemplate) => {
      if (group.id === UNGROUPED_TEMPLATE_GROUP) return;
      const updatedGroup = {
        ...group,
        crn: group.crn!,
        label: editedName,
        description: editedDescription,
      };
      setGroups((prev) =>
        prev.map((g) => (g.id === group.id ? updatedGroup : g))
      );
      setEditingGroup(null);
      updateGroupingTemplate.mutate(updatedGroup, {
        onSuccess: () => {
          displayNotification!(t("notification.saving.generic_success"), "");
        },
      });
    },
    [editedName, editedDescription, updateGroupingTemplate, displayNotification, t]
  );

  const handleCancel = useCallback(() => {
    setEditingGroup(null);
  }, []);

  const handleDelete = useCallback(
    (group: RiskGroupingTemplate) => {
      if (group.id === UNGROUPED_TEMPLATE_GROUP) return;
      openConfirmation(
        () => {
          deleteGroupingTemplate.mutate({ crn: group.crn! });
        },
        {
          label: t("riskTemplate.groupings.delete_confirmation_label"),
          subline: t("riskTemplate.groupings.delete_confirmation_subline"),
        }
      );
    },
    [openConfirmation, deleteGroupingTemplate, t]
  );

  const availableExistingTemplates = useMemo(() => {
    if (!addingMemberToGroup) return [];
    const currentMembers = new Set(addingMemberToGroup.members || []);
    return riskTemplates
      .filter((rt) => !currentMembers.has(rt.crn))
      .map((rt) => {
        const name = rt.payload.blueprint_name || rt.payload.id;
        const memberOfOtherGroups = groups
          .filter(
            (g) =>
              g.id !== addingMemberToGroup.id &&
              g.id !== UNGROUPED_TEMPLATE_GROUP &&
              (g.members || []).includes(rt.crn)
          )
          .map((g) => g.label);
        return {
          crn: rt.crn,
          label: memberOfOtherGroups.length
            ? `${name} (${memberOfOtherGroups.join(", ")})`
            : name,
        };
      });
  }, [addingMemberToGroup, riskTemplates, groups]);

  const handleSelectExistingMember = useCallback(
    (crn: string) => {
      const targetGroup = addingMemberToGroup;
      if (!targetGroup || targetGroup.id === UNGROUPED_TEMPLATE_GROUP) return;

      // A hazard template may belong to multiple chapter templates at once,
      // so this only adds membership to the target group without touching
      // any group(s) it already belongs to.
      const updatedDest = {
        ...targetGroup,
        crn: targetGroup.crn!,
        members: [...(targetGroup.members || []), crn],
      };

      setGroups((prev) =>
        prev.map((g) => (g.id === updatedDest.id ? updatedDest : g))
      );
      setAddingMemberToGroup(null);

      updateGroupingTemplate.mutate(updatedDest, {
        onSuccess: () => {
          displayNotification!(t("notification.saving.generic_success"), "");
        },
        onError: () => {
          setGroups(buildGroups(groupings, riskTemplates));
        },
      });
    },
    [
      addingMemberToGroup,
      updateGroupingTemplate,
      displayNotification,
      t,
      groupings,
      riskTemplates,
      buildGroups,
    ]
  );

  const handleCreateAndAddMember = useCallback(
    (name: string, description: string) => {
      const targetGroup = addingMemberToGroup;
      if (!targetGroup || targetGroup.id === UNGROUPED_TEMPLATE_GROUP) return;

      createRiskTemplateForGroup.mutate(
        { blueprint_name: name, blueprint_description: description },
        {
          onSuccess: (created) => {
            const updatedDest = {
              ...targetGroup,
              crn: targetGroup.crn!,
              members: [...(targetGroup.members || []), created.crn],
            };
            setGroups((prev) =>
              prev.map((g) => (g.id === updatedDest.id ? updatedDest : g))
            );
            setAddingMemberToGroup(null);
            updateGroupingTemplate.mutate(updatedDest, {
              onSuccess: () => {
                displayNotification!(
                  t("notification.saving.generic_success"),
                  ""
                );
              },
            });
          },
        }
      );
    },
    [
      addingMemberToGroup,
      createRiskTemplateForGroup,
      updateGroupingTemplate,
      displayNotification,
      t,
    ]
  );

  const handleRemoveMember = useCallback(
    (group: RiskGroupingTemplate, crn: string) => {
      if (group.id === UNGROUPED_TEMPLATE_GROUP) return;
      openConfirmation(
        () => {
          const updatedGroup = {
            ...group,
            crn: group.crn!,
            members: (group.members || []).filter((m) => m !== crn),
          };
          setGroups((prev) =>
            prev.map((g) => (g.id === updatedGroup.id ? updatedGroup : g))
          );
          updateGroupingTemplate.mutate(updatedGroup, {
            onSuccess: () => {
              displayNotification!(
                t("notification.saving.generic_success"),
                ""
              );
            },
            onError: () => {
              setGroups(buildGroups(groupings, riskTemplates));
            },
          });
        },
        {
          label: t("riskTemplate.groupings.remove_member_confirmation_label"),
          subline: t(
            "riskTemplate.groupings.remove_member_confirmation_subline"
          ),
        }
      );
    },
    [
      openConfirmation,
      updateGroupingTemplate,
      displayNotification,
      t,
      groupings,
      riskTemplates,
      buildGroups,
    ]
  );

  if (groups.length === 0) {
    return (
      <div className="text-center border-2 border-gray-300 border-dashed rounded-lg p-8 mt-4">
        <p className="text-sm text-gray-500">
          {t("riskTemplate.groupings.empty_state")}
        </p>
      </div>
    );
  }

  return (
    <div className="risk-grouping-templates mt-4">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="all-grouping-templates" type="GROUP">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {groups.map((group, groupIndex) => (
                <Draggable
                  key={group.id}
                  index={groupIndex}
                  draggableId={group.id}
                  isDragDisabled={group.id === UNGROUPED_TEMPLATE_GROUP}
                >
                  {(providedGroup) => (
                    <div
                      ref={providedGroup.innerRef}
                      {...providedGroup.draggableProps}
                      {...providedGroup.dragHandleProps}
                      className="mb-8"
                    >
                      <div className="flex center items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-6 h-6 mr-2"
                        >
                          {group.id !== UNGROUPED_TEMPLATE_GROUP && (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                            />
                          )}
                        </svg>
                        <div
                          className="bg-white shadow sm:rounded-lg w-full"
                          style={{ overflow: "visible" }}
                        >
                          <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
                            <div className="-ml-4 -mt-4 flex justify-between items-center flex-wrap sm:flex-nowrap">
                              <div className="ml-4 mt-4 w-full">
                                <div className="flex items-center mb-2 justify-between">
                                  {editingGroup === group.id ? (
                                    <div className="text-sm text-gray-500 w-full">
                                      <b>{t("risks.group.group")}:</b>{" "}
                                      {/* @ts-ignore */}
                                      <TextInput
                                        id="label"
                                        name="label"
                                        label=""
                                        required
                                        defaultValue={group.label}
                                        uncontrolled
                                        // @ts-ignore
                                        onChange={(e) =>
                                          setEditedName(e.target.value)
                                        }
                                      />
                                    </div>
                                  ) : (
                                    <h3 className="text-lg leading-6 font-medium text-gray-900 mr-2">
                                      <b>{t("risks.group.group")} #{groupIndex + 1}:</b>{" "}
                                      {group.label}
                                    </h3>
                                  )}

                                  {group.id !== UNGROUPED_TEMPLATE_GROUP && (
                                    <div
                                      className="text-right flex justify-center items-center float-right"
                                      style={{ overflow: "visible" }}
                                    >
                                      {(!editingGroup ||
                                        editingGroup !== group.id) && (
                                        <div
                                          className="relative flex justify-end items-center"
                                          style={{ overflow: "visible" }}
                                        >
                                          <button
                                            type="button"
                                            className="w-8 h-8 inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                                            ref={(el) =>
                                              (buttonRef.current[group.id] = el)
                                            }
                                            onClick={() => {
                                              setShowOptionsForGroup(
                                                showOptionsForGroup === group.id
                                                  ? null
                                                  : group.id
                                              );
                                            }}
                                          >
                                            <span className="sr-only">
                                              Open options
                                            </span>
                                            <svg
                                              className="w-5 h-5"
                                              xmlns="http://www.w3.org/2000/svg"
                                              viewBox="0 0 20 20"
                                              fill="currentColor"
                                            >
                                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                            </svg>
                                          </button>
                                          {showOptionsForGroup === group.id && (
                                            <div ref={optionsRef}>
                                              <GroupingTemplateOptions
                                                onClose={() =>
                                                  setShowOptionsForGroup(null)
                                                }
                                                onEdit={() => handleEdit(group)}
                                                onDelete={() =>
                                                  handleDelete(group)
                                                }
                                                onAddMember={() =>
                                                  setAddingMemberToGroup(group)
                                                }
                                              />
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      {editingGroup === group.id && (
                                        <>
                                          <a
                                            className="px-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                                            onClick={handleCancel}
                                          >
                                            {t("risks.group.cancel")}
                                          </a>
                                          <PrimaryButton
                                            onClick={() => handleSave(group)}
                                          >
                                            {t("risks.group.save")}
                                          </PrimaryButton>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="mt-1 text-sm text-gray-500">
                                  <div>
                                    <span>
                                      <b>
                                        {t("form.mitigations.description")}:
                                      </b>{" "}
                                      {editingGroup === group.id ? (
                                        <TextArea
                                          required
                                          id="description"
                                          name="description"
                                          label=""
                                          defaultValue={group.description}
                                          // @ts-ignore
                                          onChange={(e) =>
                                            setEditedDescription(
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : (
                                        group.description
                                      )}
                                    </span>
                                  </div>
                                  <Droppable
                                    droppableId={group.id}
                                    type="RISK"
                                  >
                                    {(providedRisks) => (
                                      <div
                                        {...providedRisks.droppableProps}
                                        ref={providedRisks.innerRef}
                                      >
                                        <Collapsible
                                          open={
                                            group.id === UNGROUPED_TEMPLATE_GROUP
                                          }
                                          label={t(
                                            "riskTemplate.groupings.members_in_group",
                                            {
                                              group: group.label,
                                            }
                                          )}
                                        >
                                          {(group.members || [])
                                            .map((crn) =>
                                              riskTemplates.find(
                                                (rt) => rt.crn === crn
                                              )
                                            )
                                            .filter(Boolean).length ? (
                                            <>
                                              {(group.members || [])
                                                .map((crn) =>
                                                  riskTemplates.find(
                                                    (rt) => rt.crn === crn
                                                  )
                                                )
                                                .filter(Boolean)
                                                .map((template, idx) => (
                                                  <RiskGroupingTemplateMemberRow
                                                    key={template!.crn}
                                                    template={template!}
                                                    index={idx}
                                                    onRemove={
                                                      group.id !==
                                                      UNGROUPED_TEMPLATE_GROUP
                                                        ? () =>
                                                            handleRemoveMember(
                                                              group,
                                                              template!.crn
                                                            )
                                                        : undefined
                                                    }
                                                  />
                                                ))}
                                              {providedRisks.placeholder}
                                            </>
                                          ) : (
                                            <>
                                              <div className="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none">
                                                {providedRisks.placeholder}
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                  strokeWidth={1.5}
                                                  stroke="currentColor"
                                                  className="mx-auto h-8 w-8 text-gray-400"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v2.25A2.25 2.25 0 0 0 6 10.5Zm0 9.75h2.25A2.25 2.25 0 0 0 10.5 18v-2.25a2.25 2.25 0 0 0-2.25-2.25H6a2.25 2.25 0 0 0-2.25 2.25V18A2.25 2.25 0 0 0 6 20.25Zm9.75-9.75H18a2.25 2.25 0 0 0 2.25-2.25V6A2.25 2.25 0 0 0 18 3.75h-2.25A2.25 2.25 0 0 0 13.5 6v2.25a2.25 2.25 0 0 0 2.25 2.25Z"
                                                  />
                                                </svg>
                                                <span className="mt-2 block text-sm font-semibold text-gray-400">
                                                  {t(
                                                    "riskTemplate.groupings.dnd_hint"
                                                  )}
                                                </span>
                                              </div>
                                            </>
                                          )}
                                        </Collapsible>
                                      </div>
                                    )}
                                  </Droppable>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <AddRiskTemplateToGroupModal
        show={!!addingMemberToGroup}
        onClose={() => setAddingMemberToGroup(null)}
        availableTemplates={availableExistingTemplates}
        onSelectExisting={handleSelectExistingMember}
        onCreateNew={handleCreateAndAddMember}
        isSubmitting={createRiskTemplateForGroup.isLoading}
      />
    </div>
  );
};
